import React, { useEffect, useRef, useState } from 'react';
import { InventoryForm } from "./InventoryForm";
import { InventoryItemCartExpandable } from "./InventoryItemCart";
import { moveCartActionsStyle, moveCartPanelStyle, moveCartScrollStyle } from './moveCartLayout';
import useInventoryCart from '../Hooks/useInventoryCart';
import Loader from '../../../components/Common/Loader';
import { LaboratoryProtocolSelector } from './LaboratoryProtocolSelector';
import { ProtocolAssigment } from '../../../api/Inventory/Entities/InventoryEntities';
import { useTranslation } from 'react-i18next';
import { useLaboratoryProtocolInventory } from '../Hooks/useLaboratoryProtocolInventory';
import { ErrorMessage } from '../../../components/Notifications/Notifications';
import { markPendingSuccessNotice } from '../../../components/Notifications/pendingSuccessNotice';
import { useUnsavedChangesPrompt } from '../../../components/Navigation/useUnsavedChangesPrompt';
import { useHistory } from "react-router-dom"
import InventoryOperationService from '../../../api/Inventory/InventoryOperationService';
import { EnumValue } from '../../../api/Freezer/Entities/Utils';
import useConfirmationModal from '../../../components/Modals/ConfirmationModal/useConfirmationModal';
import InventoryMedicationService from '../../../api/Inventory/InventoryMedicationService';
import { MoveTypes } from '../../../api/Inventory/Entities/InventoryMovementEntities';
import { buildProtocolCartLines, hasMissingRequiredKitNumber, isZeroQuantityAllowed } from './inventoryFormRules';
import { InventoryTab, inventoryTabTarget } from '../Pages/inventoryTabNavigation';

export function InventoryProtocolMoveForm({ title, moveId, moveType, assignmentType, onlyLocalInventory = false, useSelecteableRelease = false }) {
    const { t } = useTranslation()
    const history = useHistory();
    const [move, isLoading, actions] = useInventoryCart(moveId, moveType, assignmentType, {
        maxQuantityPerRow: 1
    }, null, null, { persistDraft: true, history })
    const [protocolState, handleLaboratoryChange, getProtocolById] = useLaboratoryProtocolInventory();
    const [selectedLaboratory, setSelectedLaboratory] = useState(null)
    const [selectedProtocol, setSelectedProtocol] = useState(null)
    const [Modal, modalActions, isModalLoading] = useConfirmationModal();
    const formRef = useRef()
    const { confirmLeave, allowNavigation } = useUnsavedChangesPrompt(move.items.length > 0, history, actions.DiscardDraft)

    useEffect(() => {
        if (selectedLaboratory === null) return
        handleLaboratoryChange(selectedLaboratory.Value)
    }, [selectedLaboratory])

    useEffect(() => {
        if (selectedProtocol === null) return
        actions.SetAssignId(selectedProtocol.Value)
    }, [selectedProtocol])

    useEffect(() => {
        async function PreselectProtocol(protocolId) {
            const protocol = await getProtocolById(protocolId)

            setSelectedProtocol(new EnumValue(protocol.id, protocol.title))
            setSelectedLaboratory(new EnumValue(protocol.laboratory.id, protocol.laboratory.title))
        }

        if (!isLoading) {
            const protocolId = move.assignment.assignId
            if (isNaN(protocolId) || protocolId < 1) return
            PreselectProtocol(protocolId)
        }
    }, [move?.id])


    const isAdjust = isZeroQuantityAllowed(moveType)

    if (moveType == undefined)
        throw new Error("MoveType not Assigned")

    function handleNewItem(item) {
        const selectedItem = item.selectedItem
        const isProtocolItem = selectedItem?.type?.toLowerCase() == 'protocol'
        const inventoryItemId = selectedItem?.inventoryItemId
        if (isProtocolItem && (inventoryItemId == undefined || inventoryItemId < 1)) {
            ErrorMessage("inventory.movements.commons.protocolItemMustExist")
            return
        }
        actions.AddItems(buildProtocolCartLines(item, ProtocolAssigment(selectedProtocol.Value)))
    }

    function onDeleteItem(item, _) {
        actions.RemoveItem(item)
    }

    function handleItemChange(quantity, item) {
        actions.UpdateQuantity(item, quantity)
    }

    async function onHandleSubmit() {
        if (hasMissingRequiredKitNumber(move.items, moveType)) {
            ErrorMessage("inventory.movements.commons.kitNumberIsRequired")
            return
        }

        await modalActions.Show({
            title: t('inventory.movements.confirmations.completeTitle'),
            textContent: t('inventory.movements.confirmations.completeTextContent'),
            buttonText: t('buttons.confirm'),
            requireComment: moveType == MoveTypes.AdjustStock.Name,
            onConfirmation: async ({ comment }) => {
                try {
                    const response = await InventoryOperationService.ExcecuteOperation({ ...move, comment: comment })
                    if (response.Succeeded) {
                        markPendingSuccessNotice("commons.successSave")
                        allowNavigation()
                        history.push(inventoryTabTarget(InventoryTab.operations))
                    }
                    else
                        ErrorMessage("errors.serverError")
                } catch (e) {
                    ErrorMessage("errors.serverError")
                }
            }
        })
    }

    async function onHandlePending() {
        await modalActions.Show({
            title: t('inventory.movements.confirmations.pendingTitle'),
            textContent: t('inventory.movements.confirmations.pendingTextContent'),
            buttonText: t('buttons.confirm'),
            onConfirmation: async () => {
                try {
                    const response = await InventoryOperationService.SavePending(move)
                    if (response.Succeeded) {
                        markPendingSuccessNotice("commons.successSave")
                        allowNavigation()
                        history.push(inventoryTabTarget(InventoryTab.operations))
                    }
                    else
                        ErrorMessage("errors.serverError")
                } catch (e) {
                    ErrorMessage("errors.serverError")
                }
            }
        })
    }

    function onHandleCancel() {
        if (!confirmLeave()) return
        history.push(inventoryTabTarget(InventoryTab.operations))
    }

    async function onHandleSearchItem(value) {
        const result = await InventoryMedicationService.Find({ term: value, onlyLocal: onlyLocalInventory, protocolId: selectedProtocol.Value, pageSize: 100 })
        return result.Value
    }

    const handleUpdateKit = (row, value) => {
        actions.UpdateItem({ ...row, assignment: { ...ProtocolAssigment(selectedProtocol.Value), kitNumber: value } })
    }
    const extraColumns = [{
        title: 'KIT', width: '9.375rem', formatter: (row, index) => {
            // An adjust line of quantity 0 empties the protocol's lot: it carries no kit (see isKitNumberRequired).
            if (isAdjust && Number(row.quantity) === 0)
                return (<div><input className='form-control' type='text' placeholder='kit number' value="" disabled /></div>)
            return (<div>
                <input className='form-control' type='text' placeholder='kit number' value={row.assignment?.kitNumber ?? ""} onChange={(args) => handleUpdateKit(row, args.target.value)} />
            </div>)
        }
    }]

    const clearProtocolLaboratory = () => {
        setSelectedLaboratory(null)
        setSelectedProtocol(null)
        actions.ClearItems()
        formRef.current.cleanForm()
    }

    return (<>
        <Loader isLoading={(isLoading && move != undefined) || (move?.id > 0 && selectedProtocol == null)}>
            <div className='max-width-1500 margin-center'>
                <div className='container-fluid form-container' style={{ fontWeight: "bolder" }}>
                    <h5><strong>{title}</strong></h5>
                </div>

                <div className='container-fluid form-container'>
                    <div className='right-side' style={{ height: 'max-content', maxWidth: 'max-content', minWidth: '40rem' }}>
                        <LaboratoryProtocolSelector
                            laboratories={protocolState.laboratories}
                            protocols={protocolState.protocols}
                            laboratoriesLoading={protocolState.laboratoriesLoading}
                            protocolsLoading={protocolState.isLoading}
                            selectedLaboratory={selectedLaboratory}
                            selectedProtocol={selectedProtocol}
                            onLaboratoryChange={setSelectedLaboratory}
                            onProtocolChange={setSelectedProtocol}
                            onClear={clearProtocolLaboratory}
                            itemsCount={move.items.length}
                        />
                        <InventoryForm
                            ref={formRef}
                            handleSubmit={handleNewItem}
                            handleSearchItem={onHandleSearchItem}
                            disabled={selectedLaboratory === null || selectedProtocol == null}
                            useSelecteableRelease={useSelecteableRelease}
                            assignmentType={assignmentType}
                            assignId={selectedProtocol?.Value}
                            allowZeroQuantity={isAdjust}
                            prefillCurrentStock={isAdjust}
                        />
                    </div>
                    <div className='left-side' style={moveCartPanelStyle}>
                        <div style={moveCartScrollStyle}>
                            <InventoryItemCartExpandable items={move.items} onDeleteItem={onDeleteItem} onItemsChange={handleItemChange} splitQuantity={true} extraColumns={extraColumns} />
                        </div>
                        <div style={moveCartActionsStyle}>
                            <button className='btn btn-secondary ml-1' style={{ marginRight: '1rem' }} onClick={onHandleCancel}>{t('buttons.cancel')}</button>
                            <button className='btn btn-warning   ml-1' style={{ marginRight: '1rem' }} disabled={!(move.items.length > 0) || isModalLoading} onClick={onHandlePending}>{t('buttons.pending')}</button>
                            <button className='btn btn-success' disabled={!(move.items.length > 0) || isModalLoading} onClick={onHandleSubmit}>{t('buttons.finish')}</button>
                        </div>
                    </div>
                </div>
            </div>
        </Loader>
        <Modal />
    </>)
}